import { useCallback, type MutableRefObject } from "react";
import {
  selectConversation,
  type DurableConversation,
} from "@/api/conversations";
import { discardPendingNativeVoiceNewChatIntent } from "@/desktop/voice-new-chat-routing";
import { durableConversationInputTarget } from "@/lib/durable-conversation-input-target";
import { hydrateConversationFrameHistory } from "@/lib/conversation-frame-history-cache";
import {
  captureInputTargetRoute,
  type InputTargetRouteCapture,
} from "@/lib/input-target-route-guard";
import { useInputBarTarget } from "@/stores/input-bar-target-store";
import { conversationBrowserErrorMessage } from "./conversation-browser-errors";

interface ConversationSelectOptions {
  online: boolean;
  mutationInFlightRef: MutableRefObject<boolean>;
  setMutatingId: (id: string | null) => void;
  setError: (message: string | null) => void;
}

/** Target the input bar at one durable chat, in any project, and load its
 *  history. The view never moves. `route` is captured before any await.
 *  Resolves false when another route claimed the bar meanwhile; rejects when
 *  the selection itself fails. */
export async function selectConversationAsInputTarget(
  conversation: DurableConversation,
  route: InputTargetRouteCapture,
): Promise<boolean> {
  const receipt = await selectConversation(conversation.id);
  if (!route.isCurrentScope()) return false;
  if (
    receipt.conversation_id !== conversation.id ||
    receipt.project_id !== conversation.project_id ||
    receipt.project_agent_id !== conversation.project_agent_id
  ) {
    throw new TypeError("Invalid chat selection response");
  }
  if (!route.routeIsCurrent()) return false;
  useInputBarTarget
    .getState()
    .setTarget(durableConversationInputTarget(conversation, receipt));
  discardPendingNativeVoiceNewChatIntent();
  void hydrateConversationFrameHistory({
    conversationId: receipt.conversation_id,
  }).catch(() => {});
  return true;
}

/** Select a durable chat for the next input without starting a turn. */
export function useConversationSelect({
  online,
  mutationInFlightRef,
  setMutatingId,
  setError,
}: ConversationSelectOptions) {
  return useCallback(
    async (conversation: DurableConversation): Promise<boolean> => {
      if (!online || mutationInFlightRef.current) return false;
      mutationInFlightRef.current = true;
      setMutatingId(conversation.id);
      setError(null);
      const route = captureInputTargetRoute();
      try {
        return await selectConversationAsInputTarget(conversation, route);
      } catch (error) {
        if (route.isCurrentScope()) {
          setError(conversationBrowserErrorMessage(error));
        }
        return false;
      } finally {
        mutationInFlightRef.current = false;
        if (route.isCurrentScope()) setMutatingId(null);
      }
    },
    [mutationInFlightRef, online, setError, setMutatingId],
  );
}
